import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTutorial } from '../hooks/useTutorial';
import type { RootParamList } from '../navigation/MainSwitchNavigator';
import type { RootStackParamList } from '../types/navigation';
import { useColors, ThemeColors } from '../hooks/useColors';
import { spacing, typography } from '../theme';

const { width } = Dimensions.get('window');

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  icon: keyof typeof Feather.glyphMap;
  image?: any;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: '1',
    title: 'Welcome to JadeTrade',
    description: 'Your local marketplace for fashion delivered fast like takeout. Browse products from local vendors and get them delivered to your door.',
    icon: 'shopping-bag',
  },
  {
    id: '2',
    title: 'Browse & Buy',
    description: 'Explore products by category, search for specific items, or filter by price and condition. Find exactly what you\'re looking for.',
    icon: 'search',
  },
  {
    id: '3',
    title: 'Fast Local Delivery',
    description: 'Get your items delivered quickly via the delivery service. Track your order in real-time and receive updates as it makes its way to you.',
    icon: 'truck',
  },
  {
    id: '4',
    title: 'Chat with Sellers',
    description: 'Have questions? Message sellers directly through the app. Get product details, negotiate, or ask about availability.',
    icon: 'message-circle',
  },
  {
    id: '5',
    title: 'Start Selling',
    description: 'Want to become a seller? Create your store, list products, and start selling to customers in your area. Set up is quick and easy!',
    icon: 'dollar-sign',
  },
];

export default function TutorialScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute();
  const colors = useColors();
  const styles = createStyles(colors);
  const { markTutorialComplete } = useTutorial();
  const [currentStep, setCurrentStep] = useState(0);
  
  // Check if accessed from profile (not first-time onboarding)
  // Use the route parameter if provided, otherwise fall back to canGoBack check
  const routeParams = (route as any).params;
  console.log('🚀🚀🚀 [NEW CODE LOADED] TutorialScreen Route params:', routeParams);
  console.log('🚀🚀🚀 [NEW CODE] isFirstTime from params:', routeParams?.isFirstTime);
  const isFromProfile = routeParams?.isFirstTime === true ? false : navigation.canGoBack();
  console.log('🚀🚀🚀 [NEW CODE] isFromProfile:', isFromProfile, 'canGoBack:', navigation.canGoBack());

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    console.log('🚀🚀🚀 [NEW CODE] Skip button pressed. isFromProfile:', isFromProfile);
    if (!isFromProfile) {
      await markTutorialComplete();
      console.log('🚀🚀🚀 [NEW CODE] Tutorial marked as complete, navigating to Main');
      navigation.replace('Main');
    } else {
      navigation.goBack();
    }
  };

  const handleFinish = async () => {
    console.log('🚀🚀🚀 [NEW CODE] Finish button pressed. isFromProfile:', isFromProfile);
    if (!isFromProfile) {
      await markTutorialComplete();
      console.log('🚀🚀🚀 [NEW CODE] Tutorial marked as complete, navigating to Main');
      navigation.replace('Main');
    } else {
      navigation.goBack();
    }
  };

  const step = TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]} edges={[]}>
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        {/* Skip Button */}
        <View style={styles.skipContainer}>
          <TouchableOpacity onPress={handleSkip} style={styles.skipButton}>
            <Text style={styles.skipText}>Skip</Text>
          </TouchableOpacity>
        </View>

        {/* Content */}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Icon/Image */}
          <View style={styles.iconContainer}>
            {isFirstStep ? (
              <View style={[styles.logoCircle, { backgroundColor: '#000000' }]}>
                <Image
                  source={require('../assets/logo.png')}
                  style={styles.logoImage}
                  resizeMode="cover"
                />
              </View>
            ) : (
              <View style={[styles.iconCircle, { backgroundColor: colors.primary + '20' }]}>
                <Feather name={step.icon} size={64} color={colors.primary} />
              </View>
            )}
          </View>

          {/* Title */}
          <Text style={styles.title}>{step.title}</Text>

          {/* Description */}
          <Text style={styles.description}>{step.description}</Text>

          {/* Progress Dots */}
          <View style={styles.dotsContainer}>
            {TUTORIAL_STEPS.map((_, index) => (
              <View
                key={index}
                style={[
                  styles.dot,
                  index === currentStep && styles.dotActive,
                  { backgroundColor: index === currentStep ? colors.primary : colors.textLight + '40' },
                ]}
              />
            ))}
          </View>
        </ScrollView>

        {/* Navigation Buttons */}
        <View style={styles.buttonContainer}>
          {!isFirstStep && (
            <TouchableOpacity
              onPress={handlePrevious}
              style={[styles.button, styles.buttonSecondary]}
            >
              <Text style={[styles.buttonText, styles.buttonTextSecondary]}>Previous</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            onPress={handleNext}
            style={[styles.button, styles.buttonPrimary, { backgroundColor: colors.primary }]}
          >
            <Text style={styles.buttonText}>
              {isLastStep ? 'Get Started' : 'Next'}
            </Text>
            {!isLastStep && (
              <Feather name="arrow-right" size={20} color={colors.white} style={styles.buttonIcon} />
            )}
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    skipContainer: {
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.xl * 2,
      alignItems: 'flex-end',
    },
    skipButton: {
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.md,
    },
    skipText: {
      ...typography.body,
      fontSize: 16,
      color: colors.textLight,
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.xl,
      paddingBottom: spacing.xl,
    },
    iconContainer: {
      marginBottom: spacing.xl,
      alignItems: 'center',
    },
    iconCircle: {
      width: 140,
      height: 140,
      borderRadius: 70,
      alignItems: 'center',
      justifyContent: 'center',
    },
    logoCircle: {
      width: 140,
      height: 140,
      borderRadius: 70,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    logoImage: {
      width: 125,
      height: 125,
    },
    title: {
      ...typography.title,
      fontSize: 28,
      color: colors.text,
      textAlign: 'center',
      marginBottom: spacing.md,
      fontWeight: '700',
    },
    description: {
      ...typography.body,
      fontSize: 16,
      color: colors.textLight,
      textAlign: 'center',
      lineHeight: 24,
      marginBottom: spacing.xl,
      paddingHorizontal: spacing.md,
    },
    dotsContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: spacing.lg,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      marginHorizontal: 4,
    },
    dotActive: {
      width: 24,
    },
    buttonContainer: {
      flexDirection: 'row',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.xl,
      gap: spacing.md,
    },
    button: {
      flex: 1,
      height: 52,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    buttonPrimary: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    buttonSecondary: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
    },
    buttonText: {
      ...typography.subtitle,
      fontSize: 16,
      color: colors.white,
      fontWeight: '600',
    },
    buttonTextSecondary: {
      color: colors.text,
    },
    buttonIcon: {
      marginLeft: spacing.xs,
    },
  });

